"use strict";
/**
 * @license
 * Copyright 2016 Google Inc.
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *      http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import {
  getChunkDataSizes,
  getNearIsotropicBlockSize,
  makeSliceViewChunkSpecification
} from "#src/sliceview/base.js";
import { DATA_TYPE_BYTES, DataType } from "#src/util/data_type.js";
import { getDependentTransformInputDimensions } from "#src/util/geom.js";
import * as matrix from "#src/util/matrix.js";
import { Uint64 } from "#src/util/uint64.js";
import * as vector from "#src/util/vector.js";
export { DATA_TYPE_BYTES, DataType };
export var VolumeType = /* @__PURE__ */ ((VolumeType2) => {
  VolumeType2[VolumeType2["UNKNOWN"] = 0] = "UNKNOWN";
  VolumeType2[VolumeType2["IMAGE"] = 1] = "IMAGE";
  VolumeType2[VolumeType2["SEGMENTATION"] = 2] = "SEGMENTATION";
  return VolumeType2;
})(VolumeType || {});
export const DEFAULT_MAX_VOXELS_PER_CHUNK_LOG2 = 18;
export function makeVolumeChunkSpecification(options) {
  const {
    rank,
    dataType,
    fillValue = dataType === DataType.UINT64 ? Uint64.ZERO : 0,
    compressedSegmentationBlockSize
  } = options;
  const { baseVoxelOffset = new Float32Array(rank) } = options;
  return {
    ...makeSliceViewChunkSpecification(options),
    compressedSegmentationBlockSize,
    baseVoxelOffset,
    dataType,
    fillValue
  };
}
function shouldTranscodeToCompressedSegmentation(options) {
  if (options.compressedSegmentationBlockSize !== void 0) return false;
  if (options.volumeType !== 2 /* SEGMENTATION */ && !options.volumeSourceOptions.discreteValues) {
    return false;
  }
  switch (options.dataType) {
    case DataType.UINT32:
    case DataType.UINT64:
      break;
    default:
      return false;
  }
  switch (options.rank) {
    case 3:
      return true;
    case 4: {
      const { chunkDataSize } = options;
      if (chunkDataSize[3] !== 1) return false;
      return true;
    }
    default:
      return false;
  }
}
export function makeVolumeChunkSpecificationWithDefaultCompression(options) {
  const { rank, lowerVoxelBound, upperVoxelBound } = options;
  if (!shouldTranscodeToCompressedSegmentation(options)) {
    return makeVolumeChunkSpecification(options);
  }
  let {
    volumeSourceOptions: { displayRank, multiscaleToViewTransform },
    chunkToMultiscaleTransform,
    chunkToViewTransform
  } = options;
  if (chunkToViewTransform === void 0) {
    chunkToViewTransform = matrix.multiply(
      new Float32Array(rank * displayRank),
      displayRank,
      //
      multiscaleToViewTransform,
      displayRank,
      //
      chunkToMultiscaleTransform,
      rank + 1,
      //
      displayRank,
      rank,
      rank
    );
  }
  const { maxCompressedSegmentationBlockSize, chunkDataSize } = options;
  return makeVolumeChunkSpecification({
    ...options,
    compressedSegmentationBlockSize: Float32Array.from(
      getNearIsotropicBlockSize({
        rank,
        chunkToViewTransform,
        displayRank,
        lowerVoxelBound,
        upperVoxelBound,
        maxVoxelsPerChunkLog2: 9,
        maxBlockSize: maxCompressedSegmentationBlockSize === void 0 ? chunkDataSize : vector.min(
          new Uint32Array(rank),
          chunkDataSize,
          maxCompressedSegmentationBlockSize
        )
      })
    )
  });
}
export function makeDefaultVolumeChunkSpecifications(options) {
  const { rank } = options;
  const {
    volumeSourceOptions: {
      displayRank,
      multiscaleToViewTransform,
      modelChannelDimensionIndices
    },
    chunkToMultiscaleTransform
  } = options;
  const chunkToViewTransform = matrix.multiply(
    new Float32Array(displayRank * rank),
    displayRank,
    //
    multiscaleToViewTransform,
    displayRank,
    //
    chunkToMultiscaleTransform,
    rank + 1,
    //
    displayRank,
    rank,
    rank
  );
  let { minBlockSize } = options;
  if (minBlockSize === void 0) {
    minBlockSize = new Uint32Array(rank);
    minBlockSize.fill(1);
  } else {
    minBlockSize = new Uint32Array(minBlockSize);
  }
  const { lowerVoxelBound, upperVoxelBound } = options;
  if (modelChannelDimensionIndices.length !== 0) {
    for (const chunkDim of getDependentTransformInputDimensions(
      chunkToMultiscaleTransform,
      rank,
      modelChannelDimensionIndices
    )) {
      let size = upperVoxelBound[chunkDim];
      if (lowerVoxelBound !== void 0) {
        size -= lowerVoxelBound[chunkDim];
      }
      minBlockSize[chunkDim] = size;
    }
  }
  const {
    chunkDataSizes = getChunkDataSizes({
      ...options,
      minBlockSize,
      chunkToViewTransform,
      displayRank
    })
  } = options;
  return chunkDataSizes.map(
    (chunkDataSize) => makeVolumeChunkSpecificationWithDefaultCompression({
      ...options,
      chunkDataSize,
      chunkToViewTransform
    })
  );
}
export const VOLUME_RPC_ID = "volume";
